import { useState } from 'react';
import styles from './styles.module.scss';

/**
 * Cookie Preferences Modal
 * Allows users to granularly manage cookie preferences
 * Supports necessary, analytics, marketing, and preferences cookies
 */
export default function CookiePreferencesModal({ isOpen, onClose, onSave }) {
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    preferences: false,
  });

  const handleToggle = (cookieType) => {
    if (cookieType === 'necessary') return; // Necessary cannot be toggled
    setPreferences(prev => ({
      ...prev,
      [cookieType]: !prev[cookieType]
    }));
  };

  const handleSavePreferences = () => {
    onSave(preferences);
    onClose();
  };

  const handleRejectAll = () => {
    // Only keep necessary cookies
    onSave({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    });
    onClose();
  };

  const handleAcceptAll = () => {
    // Accept all cookie types
    onSave({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Cookie Preferences</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={styles.content}>
          <p className={styles.description}>
            Manage your cookie preferences. You can enable or disable each category below.
          </p>

          <div className={styles.cookieList}>
            {/* Necessary Cookies */}
            <div className={styles.cookieItem}>
              <div className={styles.cookieHeader}>
                <label className={styles.cookieLabel}>
                  <input
                    type="checkbox"
                    checked={preferences.necessary}
                    disabled={true}
                    onChange={() => handleToggle('necessary')}
                    className={styles.checkbox}
                  />
                  <span className={styles.cookieName}>Necessary</span>
                </label>
                <span className={styles.badge}>Always required</span>
              </div>
              <p className={styles.cookieDescription}>
                Essential cookies that are always enabled. These cookies are necessary for the website to function and cannot be disabled.
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className={styles.cookieItem}>
              <label className={styles.cookieLabel}>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={() => handleToggle('analytics')}
                  className={styles.checkbox}
                />
                <span className={styles.cookieName}>Analytics</span>
              </label>
              <p className={styles.cookieDescription}>
                Google Analytics helps us understand how recruitment professionals discover our services and which job specialisms are most popular, allowing us to optimise your experience.
              </p>
            </div>

            {/* Marketing Cookies */}
            <div className={styles.cookieItem}>
              <label className={styles.cookieLabel}>
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={() => handleToggle('marketing')}
                  className={styles.checkbox}
                />
                <span className={styles.cookieName}>Marketing</span>
              </label>
              <p className={styles.cookieDescription}>
                Google Ads and social media pixels allow us to reach relevant candidates and clients with targeted recruitment opportunities that match their career interests and professional profiles.
              </p>
            </div>

            {/* Preferences Cookies */}
            <div className={styles.cookieItem}>
              <label className={styles.cookieLabel}>
                <input
                  type="checkbox"
                  checked={preferences.preferences}
                  onChange={() => handleToggle('preferences')}
                  className={styles.checkbox}
                />
                <span className={styles.cookieName}>Preferences</span>
              </label>
              <p className={styles.cookieDescription}>
                Herefish tracking personalises your candidate journey and provides relevant job recommendations based on your interaction history and professional interests.
              </p>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button 
            className={styles.buttonRejectAll}
            onClick={handleRejectAll}
          >
            Reject all
          </button>
          <button 
            className={styles.buttonSave}
            onClick={handleSavePreferences}
          >
            Save preferences
          </button>
          <button 
            className={styles.buttonAcceptAll}
            onClick={handleAcceptAll}
          >
            Accept all
          </button>
        </div>
      </div>
    </>
  );
}
